/**
 * Image Search — storefront behaviour.
 *
 * Vanilla custom element, no dependencies, no theme internals touched. It is additive
 * by construction: everything it owns lives inside its own <image-search> subtree, so
 * if this file fails to load the store's ordinary search is exactly what it was.
 *
 * THE ONE NON-OBVIOUS PIECE is the resize. A live multi-product benchmark found that
 * preserving a 1024px source kept 95.1% grid recognition, while a 512px source
 * fell to 92.7%. The browser therefore uploads 1024px for product detection; the server
 * then crops each detected item and normalizes that crop to the catalogue index's 512px
 * preprocessing width before embedding it.
 *
 * The server re-reads the real width from the uploaded header and refuses a mismatch.
 * Nothing here is trusted; this is the fast path, not the guarantee.
 */
(function () {
  "use strict";

  if (customElements.get("image-search")) return;

  /** Shopify's product JSON is public and must stay inside the current market route. */
  function productJsonUrl(handle) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root !== "string" || root.charAt(0) !== "/") root = "/";
    if (root.charAt(root.length - 1) !== "/") root += "/";
    return root + "products/" + encodeURIComponent(handle) + ".js";
  }
  var JPEG_QUALITY = 0.85;
  var PRODUCT_HYDRATION_CONCURRENCY = 4;
  var PRODUCT_HYDRATION_ATTEMPTS = 4;
  var PRODUCT_HYDRATION_ATTEMPT_TIMEOUT_MS = 5000;
  var PRODUCT_HYDRATION_TOTAL_TIMEOUT_MS = 15000;
  // The server stops its own work at 52 seconds. The browser keeps a little transport
  // headroom, but must not spin forever when a proxy accepts the request and then
  // blackholes the response (or its JSON body).
  var IMAGE_SEARCH_REQUEST_TIMEOUT_MS = 60000;

  function abortReason(signal, fallbackName, fallbackMessage) {
    return (
      (signal && signal.reason) ||
      new DOMException(fallbackMessage || "The operation was aborted", fallbackName || "AbortError")
    );
  }

  /** Race a browser operation with cancellation and dispose late-arriving resources. */
  function abortableResult(promise, signal, disposeLateValue) {
    if (!signal) return promise;
    return new Promise(function (resolve, reject) {
      var settled = false;
      function cleanup() {
        signal.removeEventListener("abort", abort);
      }
      function abort() {
        if (settled) return;
        settled = true;
        cleanup();
        reject(abortReason(signal));
      }
      if (signal.aborted) {
        abort();
      } else {
        signal.addEventListener("abort", abort, { once: true });
      }
      promise.then(
        function (value) {
          if (settled) {
            if (disposeLateValue) disposeLateValue(value);
            return;
          }
          settled = true;
          cleanup();
          resolve(value);
        },
        function (error) {
          if (settled) return;
          settled = true;
          cleanup();
          reject(error);
        },
      );
    });
  }

  /** One child signal linked to its parent and a bounded timer. */
  function linkedTimeout(parentSignal, milliseconds, message) {
    var controller = new AbortController();
    function abortFromParent() {
      controller.abort(abortReason(parentSignal));
    }
    if (parentSignal && parentSignal.aborted) abortFromParent();
    else if (parentSignal) parentSignal.addEventListener("abort", abortFromParent, { once: true });
    var timer = setTimeout(function () {
      controller.abort(new DOMException(message, "TimeoutError"));
    }, milliseconds);
    return {
      signal: controller.signal,
      abort: function (reason) {
        controller.abort(reason);
      },
      dispose: function () {
        clearTimeout(timer);
        if (parentSignal) parentSignal.removeEventListener("abort", abortFromParent);
      },
    };
  }

  function abortableDelay(milliseconds, signal) {
    return new Promise(function (resolve, reject) {
      if (signal && signal.aborted) {
        reject(abortReason(signal));
        return;
      }
      var timer = setTimeout(done, milliseconds);
      function abort() {
        clearTimeout(timer);
        cleanup();
        reject(abortReason(signal));
      }
      function cleanup() {
        if (signal) signal.removeEventListener("abort", abort);
      }
      function done() {
        cleanup();
        resolve();
      }
      if (signal) signal.addEventListener("abort", abort, { once: true });
    });
  }

  function hydrationRetryDelay(response, attempt) {
    var raw = response && response.headers && response.headers.get("Retry-After");
    if (raw) {
      var seconds = Number(raw);
      if (Number.isFinite(seconds) && seconds >= 0) return Math.min(30000, seconds * 1000);
      var date = Date.parse(raw);
      if (Number.isFinite(date)) return Math.min(30000, Math.max(0, date - Date.now()));
    }
    return 1000 * Math.pow(2, attempt);
  }

  function t(el, key, fallback) {
    var v = el.getAttribute("data-i18n-" + key);
    return v || fallback;
  }

  /**
   * A refusal that knows which sentence to show.
   *
   * `prepare` can fail for three unrelated reasons — the file would not decode, the
   * photo is too small to be worth searching, the photo is a sliver rather than a
   * picture — and the shopper's next move differs for each. Every one of them used to
   * surface as "that image could not be read", which is only true of the first.
   */
  function prepareError(key, fallback) {
    var e = new Error(fallback);
    e.i18nKey = key;
    e.i18nFallback = fallback;
    return e;
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: currency || "USD",
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /**
   * Shopify image URLs accept a width parameter, but many already carry a query string
   * (`?v=`), so the parameter has to be MERGED. Naive concatenation produces
   * `...?v=1?width=400`, which Shopify does not parse — it serves the full-resolution
   * original, and a results grid quietly downloads twelve multi-megabyte images.
   */
  function sized(url, width) {
    if (!url) return "";
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set("width", String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  class ImageSearch extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this._lifecycleController = new AbortController();
      var lifecycleSignal = this._lifecycleController.signal;

      this.endpoint = this.dataset.endpoint;
      this.shop = this.dataset.shop;
      this.readConfig(this.dataset);

      this.fileInput = this.querySelector("[data-image-search-file]");
      this.panel = this.querySelector("[data-image-search-panel]");
      this.hint = this.querySelector("[data-image-search-hint]");
      this.statusEl = this.querySelector("[data-image-search-status]");
      this.subEl = this.querySelector("[data-image-search-sub]");
      this.previewEl = this.querySelector("[data-image-search-preview]");
      this.previewWrap = this.querySelector("[data-image-search-preview-wrap], .image-search__preview");
      if (this.previewWrap && (!this.previewEl || !this.previewEl.getAttribute("src"))) {
        this.previewWrap.hidden = true;
      }
      this.progress = this.querySelector("[data-image-search-progress]");
      this.grid = this.querySelector("[data-image-search-grid]");
      this.empty = this.querySelector("[data-image-search-empty]");
      this.noMatch = this.querySelector("[data-image-search-nomatch]");
      this.unavailable = this.querySelector("[data-image-search-unavailable]");

      this.placeTrigger();

      // Event listeners below intentionally use ordinary functions for theme-browser
      // compatibility, so keep an explicit instance reference.
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      var self = this;
      // Queried from the whole section, not this subtree: `placeTrigger` may have moved
      // the camera button into the theme's own input row, which is outside it.
      var scope = this.closest("form") || this;
      scope.querySelectorAll("[data-image-search-open]").forEach(function (b) {
        b.addEventListener("click", function () {
          self.fileInput.click();
        }, { signal: lifecycleSignal });
      });
      this.querySelector("[data-image-search-clear]").addEventListener("click", function () {
        self.reset(true);
      }, { signal: lifecycleSignal });
      this.fileInput.addEventListener("change", function () {
        var file = self.fileInput.files && self.fileInput.files[0];
        if (file) self.run(file);
      }, { signal: lifecycleSignal });

      // Impact keeps the drawer in the DOM when its close button, overlay, or focus
      // trap dismisses it. `disconnectedCallback` therefore cannot be the close hook.
      // The drawer emits `toggle` synchronously for every dismissal path; cancelling
      // there stops paid work and releases the hidden preview immediately.
      this._drawer = this.closest("search-drawer");
      if (this._drawer) {
        this._drawer.addEventListener("toggle", function (event) {
          if (event.target === self._drawer && !self._drawer.hasAttribute("open")) {
            self.reset();
          }
        }, { signal: lifecycleSignal });
      }

      // Escape returns the shopper to ordinary search rather than closing the whole
      // drawer out from under them — a photo search is a mode, and the way out of a
      // mode should be the smallest step back.
      this.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !self.panel.hidden) {
          e.stopPropagation();
          self.reset(true);
        }
      }, { signal: lifecycleSignal });
    }

    /**
     * Sizing rules, read from data attributes with defaults that MUST match the
     * snippet's.
     *
     * Split out of `connectedCallback` so a test can drive it — both with the values
     * the snippet actually ships and with none at all. The bug this feature shipped
     * with was not a broken function; it was two correct halves that disagreed about
     * the same number, each tested alone. A default that silently drifts from the
     * attribute beside it is the same failure wearing a different hat.
     */
    readConfig(dataset) {
      this.targetWidth = parseInt(dataset.targetWidth, 10) || 1024;
      this.maxBytes = parseInt(dataset.maxBytes, 10) || 1500000;
      this.minSourceWidth = parseInt(dataset.minSourceWidth, 10) || 128;
      this.maxAspect = parseInt(dataset.maxAspect, 10) || 8;
      this.resultLimit = parseInt(dataset.resultLimit, 10) || 12;
    }

    /**
     * Move the camera button into the theme's search field.
     *
     * WHY IT IS MOVED RATHER THAN RENDERED THERE. The button belongs inside
     * `.search-input` — that is where shoppers look for it, and it is the layout every
     * comparable feature uses — but the panel below belongs after the field. Rendering
     * one snippet in two places would split this feature across two Liquid call sites
     * and let them drift; rendering the field's markup from our snippet would mean
     * vendoring the theme's input.
     *
     * So the section patch stays a single `{% render %}` and the element puts its own
     * trigger where it goes. If the field is not found — a theme that renamed it, or a
     * future redesign — nothing throws and the button simply stays in the hint row,
     * which still works. That is the whole failure mode.
     */
    placeTrigger() {
      var trigger = this.querySelector(".image-search__trigger");
      if (!trigger) return;
      var form = this.closest("form");
      var field = form && form.querySelector(".search-input");
      if (!field) return;

      // Before the theme's close button when there is one, so the reading order stays
      // "type here, or use a photo, or close" rather than putting a new control after
      // the dismiss action.
      var close = field.querySelector('[is="close-button"]');
      if (close) field.insertBefore(trigger, close);
      else field.appendChild(trigger);
    }

    /** Release every browser-owned reference to the shopper's selected image. */
    releaseImageResources() {
      if (this._objectUrl) {
        URL.revokeObjectURL(this._objectUrl);
        this._objectUrl = null;
      }
      if (this.previewEl) this.previewEl.removeAttribute("src");
      if (this.previewWrap) this.previewWrap.hidden = true;
      if (this.fileInput) this.fileInput.value = "";
    }

    /** Cancel the current attempt without deciding what the result panel should show. */
    cancelAttempt() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      this._token = null;
      this.releaseImageResources();
    }

    /** Finish only the attempt that still owns the current token and controller. */
    completeAttempt(token, controller) {
      if (this._token !== token || this._abortController !== controller) return;
      this._token = null;
      this._abortController = null;
      this.releaseImageResources();
    }

    /** Everything the panel owns, returned to the state the page loaded in. */
    reset(restoreSearchFocus) {
      this.cancelAttempt();
      this.panel.hidden = true;
      this.grid.hidden = true;
      this.grid.innerHTML = "";
      this.empty.hidden = true;
      this.progress.hidden = false;
      if (this.hint) this.hint.hidden = false;
      if (restoreSearchFocus) {
        var form = this.closest("form");
        var focusTarget = form && form.querySelector('input[type="search"]');
        if (!focusTarget) {
          focusTarget = (form || this).querySelector("[data-image-search-open]");
        }
        if (focusTarget && !focusTarget.disabled) {
          focusTarget.focus({ preventScroll: true });
        }
      }
    }

    disconnectedCallback() {
      this.cancelAttempt();
      if (this._lifecycleController) this._lifecycleController.abort();
      this._lifecycleController = null;
      this._drawer = null;
      this._wired = false;
    }

    setStatus(main, sub) {
      this.statusEl.textContent = main || "";
      this.subEl.textContent = sub || "";
    }

    /**
     * Reveal the closing block, with or without the no-match advice.
     *
     * `withNoMatchCopy` is false for every error path. "Try another angle or a plainer
     * background" is good advice when we searched and found nothing, and misdirection
     * when we never reached the server -- it blames the shopper's photo for our outage.
     * The retry button stays either way, because it is the useful action in both.
     */
    showEmpty(reason) {
      if (this.noMatch) this.noMatch.hidden = reason !== "no-match";
      if (this.unavailable) this.unavailable.hidden = reason !== "unavailable";
      this.empty.hidden = false;
    }

    async run(file) {
      // Every run gets a token. An earlier, slower search must not overwrite the grid
      // of a later one -- the shopper picked a second photo because they wanted the
      // second answer.
      this.cancelAttempt();
      var controller = new AbortController();
      this._abortController = controller;
      var token = {};
      this._token = token;

      try {
        this.panel.hidden = false;
        this.empty.hidden = true;
        this.grid.hidden = true;
        this.grid.innerHTML = "";
        this.progress.hidden = false;
        if (this.hint) this.hint.hidden = true;

        if (this._objectUrl) URL.revokeObjectURL(this._objectUrl);
        this._objectUrl = URL.createObjectURL(file);
        this.previewEl.src = this._objectUrl;
        if (this.previewWrap) this.previewWrap.hidden = false;

        this.setStatus(
          t(this, "preparing", "Preparing your photo…"),
          t(this, "preparing-sub", "Your photo is resized before upload and isn't saved by us."),
        );

        var blob;
        try {
          blob = await this.prepare(file, controller.signal);
        } catch (e) {
          if (this._token !== token) return;
          // A refusal carries the sentence that fits it; anything else — a codec the
          // browser cannot open, a truncated file, a decoder that threw — really is
          // "could not be read", so that stays the default rather than the only answer.
          this.fail(
            t(
              this,
              (e && e.i18nKey) || "err-decode",
              (e && e.i18nFallback) || "That image could not be read. Try a JPG or PNG.",
            ),
          );
          return;
        }
        if (this._token !== token) return;

        if (blob.size > this.maxBytes) {
          this.fail(t(this, "err-large", "That image is too large."));
          return;
        }

        this.setStatus(t(this, "searching", "Finding similar products…"), "");

        var res;
        var payload = null;
        var requestDeadline = linkedTimeout(
          controller.signal,
          IMAGE_SEARCH_REQUEST_TIMEOUT_MS,
          "Photo search timed out",
        );
        try {
          res = await fetch(this.endpoint, {
            method: "POST",
            headers: { "Content-Type": blob.type, "X-Shop-Domain": this.shop },
            body: blob,
            // No cookies: this endpoint has no session and never wants one. Sending
            // credentials to a cross-origin API is how a public surface accidentally
            // starts depending on whoever happens to be logged in.
            credentials: "omit",
            mode: "cors",
            signal: requestDeadline.signal,
          });
          if (this._token !== token) return;

          try {
            payload = await abortableResult(res.json(), requestDeadline.signal);
          } catch {
            // A malformed, completed body is handled by the status checks below. An
            // aborted body is different: preserve the timeout/cancel reason so it can
            // neither hang nor be misreported as an empty response.
            if (requestDeadline.signal.aborted) throw abortReason(requestDeadline.signal);
          }
        } catch (error) {
          if (this._token !== token) return;
          if (error && error.name === "TimeoutError") {
            this.fail(t(this, "err-timeout", "Photo search took too long. Try again."));
          } else {
            this.fail(t(this, "err-network", "Couldn't reach search. Check your connection."));
          }
          return;
        } finally {
          requestDeadline.dispose();
        }
        if (this._token !== token) return;

        if (res.status === 429) {
          this.fail(
            (payload && payload.message) ||
              t(this, "err-rate", "Too many searches. Please wait a moment."),
          );
          return;
        }
        if (!res.ok || !payload) {
          this.fail(
            (payload && payload.message) ||
              t(this, "err-generic", "Photo search is unavailable right now."),
          );
          return;
        }

        if (payload.status === "no_close_match" || !payload.results || !payload.results.length) {
          this.progress.hidden = true;
          this.setStatus(t(this, "no-match", "No close match"), "");
          this.showEmpty("no-match");
          return;
        }

        try {
          await this.render(payload.results, token, controller.signal);
        } catch {
          if (this._token !== token) return;
          controller.abort();
          this.fail(t(this, "err-network", "Couldn't load current products. Try again."));
        }
      } finally {
        this.completeAttempt(token, controller);
      }
    }

    /**
     * Decode the shopper's file, orientation included.
     *
     * Two paths, and the fallback is not decoration. `createImageBitmap` with options
     * is the good path — it applies EXIF rotation during decode, and a phone photo
     * that ignores EXIF uploads sideways, which embeds as a different garment. But
     * Safari only accepted the options argument from 16.4, and older iOS either throws
     * or silently ignores it. So: try it, and on ANY failure fall through to an <img>
     * element, which browsers have auto-oriented from EXIF by default for years. The
     * fallback is therefore orientation-correct too, not merely functional.
     *
     * The <img> path is also what makes HEIC work on iPhones: Safari decodes HEIC
     * natively, and everything after this point deals in a canvas, not a file type.
     */
    async decode(file, signal) {
      if (typeof createImageBitmap === "function") {
        try {
          return await abortableResult(
            createImageBitmap(file, { imageOrientation: "from-image" }),
            signal,
            function (bitmap) {
              if (bitmap && bitmap.close) bitmap.close();
            },
          );
        } catch {
          if (signal && signal.aborted) throw abortReason(signal);
          /* older Safari, or a codec it will only handle through <img> */
        }
      }
      return await new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        var settled = false;
        function cleanup() {
          URL.revokeObjectURL(url);
          if (signal) signal.removeEventListener("abort", abort);
        }
        function abort() {
          if (settled) return;
          settled = true;
          cleanup();
          img.src = "";
          reject(abortReason(signal));
        }
        img.onload = function () {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(img);
        };
        img.onerror = function () {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error("decode failed"));
        };
        if (signal && signal.aborted) {
          abort();
          return;
        }
        if (signal) signal.addEventListener("abort", abort, { once: true });
        img.src = url;
      });
    }

    /**
     * Scale to the measured detector-source width and re-encode as JPEG.
     *
     * THE HALVING LOOP IS FOR PHONES. A modern phone photo is ~4000px wide and the
     * target is 1024 — a 4x reduction. Every browser's one-shot `drawImage` samples
     * far too sparsely at that ratio: it effectively point-samples, so fine fabric
     * texture and print detail alias into noise. That noise is what gets embedded.
     * Halving repeatedly until within 2x of the target, then doing the last step
     * normally, keeps the detail the model actually matches on. Desktop uploads are
     * usually small enough that the loop never runs.
     */
    /**
     * The output size for a source photo, or a refusal. Pure — no canvas, no decode.
     *
     * ALWAYS the detector's source width, in both directions. This function used to leave a photo
     * narrower than the target at its own size, reasoning in a comment that upscaling
     * "invents detail the model then reads as real, and the server's tolerance band
     * accepts the honest smaller width instead". The first half is a fair worry; the
     * second half was simply untrue — the band is the target ±32px, so every source
     * under 480px wide was refused outright, and the shopper was told their photo
     * "could not be prepared for search" with no way to act on it. Small photos are
     * ordinary: a picture saved off social, a screenshot, a tight crop.
     *
     * Measured before changing it, against the live index: a catalogue photo reduced to
     * 200px and scaled back up still ranked its own product first at 0.898, from 320px
     * at 0.928, from 400px at 0.951. Upscaling costs real accuracy and degrades
     * smoothly; refusing costs the shopper the entire feature. Below `minSourceWidth`
     * the invention outweighs the picture, so that case is refused HONESTLY instead —
     * a sentence saying the photo is too small, which is something a shopper can do
     * something about.
     */
    geometryFor(srcW, srcH) {
      if (srcW < this.minSourceWidth) {
        throw prepareError(
          "err-small",
          "That photo is too small to search with. Try a larger picture.",
        );
      }

      var w = this.targetWidth;
      var h = Math.max(1, Math.round((srcH * this.targetWidth) / srcW));

      // The same shape rule the server enforces, checked here so the shopper gets a
      // sentence about the photo rather than a refusal from an API they cannot see.
      // Both orientations: a sliver is unphotographic whichever way round it lies.
      if (h > w * this.maxAspect || w > h * this.maxAspect) {
        throw prepareError(
          "err-shape",
          "That photo is too long and narrow to search with. Try a squarer crop.",
        );
      }

      return { width: w, height: h };
    }

    async prepare(file, signal) {
      var source = await this.decode(file, signal);
      var canvases = [];
      try {
        if (signal) signal.throwIfAborted();
        var srcW = source.width || source.naturalWidth;
        var srcH = source.height || source.naturalHeight;
        if (!srcW || !srcH) {
          // A decoder that returns a zero dimension has not decoded anything — an SVG
          // with no intrinsic size reaches here through the <img> path.
          throw prepareError("err-decode", "That image could not be read. Try a JPG or PNG.");
        }

        var geometry = this.geometryFor(srcW, srcH);
        var w = geometry.width;
        var h = geometry.height;

        var current = source;
        var curW = srcW;
        var curH = srcH;
        while (curW > w * 2) {
          var nextW = Math.max(w, Math.round(curW / 2));
          var nextH = Math.max(1, Math.round((curH * nextW) / curW));
          var step = document.createElement("canvas");
          canvases.push(step);
          step.width = nextW;
          step.height = nextH;
          var sctx = step.getContext("2d");
          sctx.imageSmoothingEnabled = true;
          sctx.imageSmoothingQuality = "high";
          sctx.drawImage(current, 0, 0, nextW, nextH);
          current = step;
          curW = nextW;
          curH = nextH;
        }

        var canvas = document.createElement("canvas");
        canvases.push(canvas);
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        // White matte: a PNG with transparency would otherwise composite onto black,
        // and a black silhouette is a different image to the model than the garment is.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(current, 0, 0, w, h);

        return await abortableResult(
          new Promise(function (resolve, reject) {
            canvas.toBlob(function (b) {
              if (b) resolve(b);
              else reject(new Error("encode failed"));
            }, "image/jpeg", JPEG_QUALITY);
          }),
          signal,
        );
      } finally {
        // An ImageBitmap owns decoded image memory. Every canvas owns raw shopper
        // pixels. Clear both on success AND on geometry/draw/encode failure.
        if (source && source.close) source.close();
        canvases.forEach(function (canvas) {
          canvas.width = 0;
          canvas.height = 0;
        });
      }
    }

    async hydrateProduct(handle, signal) {
      var lastError = new Error("product hydration failed");
      for (var attempt = 0; attempt < PRODUCT_HYDRATION_ATTEMPTS; attempt++) {
        var response = null;
        var attemptDeadline = linkedTimeout(
          signal,
          PRODUCT_HYDRATION_ATTEMPT_TIMEOUT_MS,
          "Shopify product request timed out",
        );
        try {
          try {
            response = await fetch(productJsonUrl(handle), {
              credentials: "same-origin",
              cache: "no-store",
              signal: attemptDeadline.signal,
            });
          } catch (e) {
            if (signal && signal.aborted) throw e;
            lastError = e;
          }

          if (response && (response.status === 404 || response.status === 410)) return null;
          if (response && response.ok) {
            try {
              // Fetch resolves after headers; body consumption must remain inside the
              // same attempt deadline or a stalled/truncated JSON body can hang forever.
              var product = await abortableResult(response.json(), attemptDeadline.signal);
              var validProduct =
                product &&
                product.handle === handle &&
                typeof product.available === "boolean" &&
                typeof product.title === "string" &&
                product.title.trim().length > 0 &&
                typeof product.url === "string" &&
                (product.url.charAt(0) === "/" || /^https?:\/\//i.test(product.url)) &&
                typeof product.price === "number" &&
                Number.isFinite(product.price) &&
                product.price >= 0;
              if (validProduct) return product;
              lastError = new Error("Shopify product JSON is incomplete or mismatched");
            } catch (e) {
              if (signal && signal.aborted) throw e;
              lastError = e;
            }
          } else if (response && response.status !== 429 && response.status < 500) {
            throw new Error("Shopify product hydration returned HTTP " + response.status);
          } else if (response) {
            lastError = new Error("Shopify product hydration returned HTTP " + response.status);
          }
        } finally {
          attemptDeadline.dispose();
        }

        if (attempt + 1 < PRODUCT_HYDRATION_ATTEMPTS) {
          await abortableDelay(hydrationRetryDelay(response, attempt), signal);
        }
      }
      throw lastError;
    }

    async hydrateProducts(results, signal) {
      var deadline = linkedTimeout(
        signal,
        PRODUCT_HYDRATION_TOTAL_TIMEOUT_MS,
        "Shopify product hydration timed out",
      );
      var products = new Array(results.length);
      var cursor = 0;
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      var self = this;
      async function worker() {
        while (true) {
          var index = cursor++;
          if (index >= results.length) return;
          products[index] = await self.hydrateProduct(results[index].handle, deadline.signal);
        }
      }
      var workerCount = Math.min(PRODUCT_HYDRATION_CONCURRENCY, results.length);
      try {
        await Promise.all(Array.from({ length: workerCount }, worker));
        return products;
      } catch (error) {
        // The grid is one result: once one authoritative hydration attempt fails, stop
        // sibling workers instead of leaving invisible storefront requests running.
        deadline.abort(error);
        throw error;
      } finally {
        deadline.dispose();
      }
    }

    /**
     * Build cards from Shopify's own product JSON.
     *
     * The API returns handles and scores only. Price, availability, currency and the
     * product URL all come from the storefront the shopper is already on, so they are
     * market-correct without this feature holding a Storefront credential or copying
     * pricing rules that would go stale.
     */
    async render(results, token, signal) {
      // Promise callbacks intentionally use ordinary functions for theme-browser
      // compatibility, so keep an explicit instance reference.
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      var self = this;
      var products = await this.hydrateProducts(results, signal);
      if (this._token !== token) return;

      products = this.purchasableProducts(products);
      var frag = document.createDocumentFragment();
      var shown = 0;

      products.forEach(function (p) {
        frag.appendChild(self.card(p));
        shown++;
      });

      this.progress.hidden = true;

      if (!shown) {
        this.setStatus(t(this, "unavailable", "We couldn't find an available match"), "");
        this.showEmpty("unavailable");
        return;
      }

      this.grid.appendChild(frag);
      this.grid.hidden = false;
      this.setStatus(
        t(this, "results", "Visually similar products"),
        shown + " " + (shown === 1 ? t(this, "result", "result") : t(this, "results-n", "results")),
      );
      // Move focus to the status line so a keyboard or screen-reader user lands on the
      // thing that changed, rather than staying on a camera button whose panel has
      // just filled with new content behind them.
      this.statusEl.setAttribute("tabindex", "-1");
      this.statusEl.focus({ preventScroll: true });
    }

    /**
     * Convert the server's small reserve pool into the actual twelve-card grid.
     * Shopify's market-aware product JSON is the final authority: sold-out and removed
     * products are skipped, and later candidates backfill their places.
     */
    purchasableProducts(products) {
      return products
        .filter(function (product) {
          return product && product.handle && product.available === true;
        })
        .slice(0, this.resultLimit);
    }

    card(p) {
      var li = document.createElement("li");
      li.className = "image-search__card";

      var a = document.createElement("a");
      a.className = "image-search__card-link";
      a.href = p.url;

      var figure = document.createElement("div");
      figure.className = "image-search__card-media";
      if (p.featured_image) {
        var img = document.createElement("img");
        img.src = sized(p.featured_image, 400);
        img.alt = p.title || "";
        img.loading = "lazy";
        img.decoding = "async";
        figure.appendChild(img);
      }

      var body = document.createElement("div");
      body.className = "image-search__card-body";

      var title = document.createElement("span");
      title.className = "image-search__card-title";
      title.textContent = p.title || "";

      var price = document.createElement("span");
      price.className = "image-search__card-price";
      price.textContent = money(p.price, window.Shopify && window.Shopify.currency && window.Shopify.currency.active);

      body.appendChild(title);
      body.appendChild(price);

      a.appendChild(figure);
      a.appendChild(body);
      li.appendChild(a);
      return li;
    }

    fail(message) {
      this.progress.hidden = true;
      this.grid.hidden = true;
      this.setStatus(message, "");
      this.showEmpty(null);
    }
  }

  customElements.define("image-search", ImageSearch);
})();
